/**
 * Ops Dashboard — mirrors apps/experts/public/admin/ops-dashboard.html. One GET
 * /api/admin/ops-dashboard call returns four independently-degradable sections
 * (latest-eval/trend, cron, deploys, llm-budget); each section renders its own
 * PENDING banner if the backend couldn't fill it, exactly like the original.
 */
import {
  AdminError,
  AdminLoading,
  AdminPageHeader,
  Mono,
  Panel,
  PendingBanner,
  StatusDot,
  Table,
  Td,
  Th,
  toneForStatus,
  Tr,
} from '../../components/primitives';
import { useOpsDashboard } from '../../data-access/useAdmin';
import { duration, isPendingSection, relTime, usd } from '../../lib/format';

const SUITES = [
  'open_ended',
  'held_out',
  'multi_turn',
  'multi_packet',
  'stress',
];

export function OpsDashboardPage() {
  const { data, isPending, isError, error, refetch, isFetching } =
    useOpsDashboard();

  return (
    <div>
      <AdminPageHeader
        actions={
          <button
            className="rounded-md border border-border-subtle px-3 py-1.5 font-inter text-content-secondary text-sm hover:bg-surface-background"
            onClick={() => void refetch()}
            type="button"
          >
            {isFetching ? 'Refreshing…' : 'Refresh'}
          </button>
        }
        subtitle={
          data
            ? `Generated ${relTime(data.generated_at)}`
            : 'Operational dashboard'
        }
        title="Ops Dashboard"
      />

      {isPending ? <AdminLoading /> : null}
      {isError ? (
        <AdminError
          message={`Failed to load: ${(error as Error)?.message ?? 'unknown'}`}
        />
      ) : null}

      {data ? (
        <div className="grid gap-4 lg:grid-cols-2">
          {/* Latest eval per suite */}
          <Panel title="Latest eval per suite">
            {isPendingSection(data.eval_runs) ? (
              <PendingBanner note={data.eval_runs.note} />
            ) : (
              <Table
                head={
                  <>
                    <Th>Suite</Th>
                    <Th>Score</Th>
                    <Th>Size</Th>
                    <Th>Ran</Th>
                  </>
                }
              >
                {SUITES.map((suite) => {
                  const run = !isPendingSection(data.eval_runs)
                    ? data.eval_runs.latest_per_suite[suite]
                    : null;
                  return (
                    <Tr key={suite}>
                      <Td>
                        <Mono>{suite}</Mono>
                      </Td>
                      <Td>{run?.score != null ? run.score.toFixed(2) : '—'}</Td>
                      <Td>{run?.suite_size ?? '—'}</Td>
                      <Td>{relTime(run?.ran_at)}</Td>
                    </Tr>
                  );
                })}
              </Table>
            )}
          </Panel>

          {/* 7d trend */}
          <Panel title="7-day eval trend">
            {isPendingSection(data.eval_runs) ? (
              <PendingBanner note={data.eval_runs.note} />
            ) : data.eval_runs.trend_7d.length === 0 ? (
              <p className="font-inter text-content-tertiary text-sm">
                No scored runs yet.
              </p>
            ) : (
              <Table
                head={
                  <>
                    <Th>Ran</Th>
                    <Th>7d moving avg</Th>
                  </>
                }
              >
                {data.eval_runs.trend_7d.slice(0, 10).map((pt) => (
                  <Tr key={pt.ran_at}>
                    <Td>{relTime(pt.ran_at)}</Td>
                    <Td>{pt.moving_avg.toFixed(3)}</Td>
                  </Tr>
                ))}
              </Table>
            )}
          </Panel>

          {/* Cron */}
          <Panel title="Recent cron executions">
            {isPendingSection(data.cron) ? (
              <PendingBanner note={data.cron.note} />
            ) : (
              <Table
                head={
                  <>
                    <Th>Job</Th>
                    <Th>Status</Th>
                    <Th>Duration</Th>
                    <Th>Started</Th>
                  </>
                }
              >
                {data.cron.executions.slice(0, 12).map((ex) => (
                  <Tr key={ex.id}>
                    <Td>
                      <Mono title={ex.job_name}>
                        {ex.job_name
                          .replace('caj-andavo-labs-', '')
                          .replace('-dev', '')}
                      </Mono>
                    </Td>
                    <Td>
                      <span className="inline-flex items-center gap-1.5">
                        <StatusDot tone={toneForStatus(ex.status)} />
                        {ex.status}
                      </span>
                    </Td>
                    <Td>{duration(ex.duration_ms)}</Td>
                    <Td>{relTime(ex.started_at)}</Td>
                  </Tr>
                ))}
              </Table>
            )}
          </Panel>

          {/* Deploys */}
          <Panel title="Recent production deploys">
            {isPendingSection(data.deploys) ? (
              <PendingBanner note={data.deploys.note} />
            ) : (
              <Table
                head={
                  <>
                    <Th>SHA</Th>
                    <Th>Branch</Th>
                    <Th>Result</Th>
                    <Th>When</Th>
                  </>
                }
              >
                {data.deploys.runs.slice(0, 12).map((run) => (
                  <Tr key={run.id}>
                    <Td>
                      {run.url ? (
                        <a
                          className="font-mono text-content-action text-xs hover:underline"
                          href={run.url}
                          rel="noreferrer"
                          target="_blank"
                        >
                          {run.sha ?? run.id.slice(0, 7)}
                        </a>
                      ) : (
                        <Mono>{run.sha ?? '—'}</Mono>
                      )}
                    </Td>
                    <Td>
                      <Mono>{run.branch ?? '—'}</Mono>
                    </Td>
                    <Td>
                      <span className="inline-flex items-center gap-1.5">
                        <StatusDot tone={toneForStatus(run.conclusion)} />
                        {run.conclusion}
                      </span>
                    </Td>
                    <Td>{relTime(run.updated_at ?? run.created_at)}</Td>
                  </Tr>
                ))}
              </Table>
            )}
          </Panel>

          {/* LLM budget */}
          <Panel className="lg:col-span-2" title="LLM spend today">
            {isPendingSection(data.llm_budget) ? (
              <PendingBanner note={data.llm_budget.note} />
            ) : data.llm_budget.today_spend.length === 0 ? (
              <p className="font-inter text-content-tertiary text-sm">
                No spend recorded today.
              </p>
            ) : (
              <Table
                head={
                  <>
                    <Th>Provider</Th>
                    <Th>Spend (USD)</Th>
                    <Th>Calls</Th>
                  </>
                }
              >
                {data.llm_budget.today_spend.map((row) => (
                  <Tr key={row.provider}>
                    <Td>
                      <Mono>{row.provider}</Mono>
                    </Td>
                    <Td>{usd(row.spend_usd)}</Td>
                    <Td>{row.calls ?? '—'}</Td>
                  </Tr>
                ))}
              </Table>
            )}
          </Panel>
        </div>
      ) : null}
    </div>
  );
}

export default OpsDashboardPage;
